import { useCallback, useContext, useEffect, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import EncoderAPI from "../api/EncoderAPI";
import MediaAPI from "../api/MediaAPI";
import { WebSocketContext } from "../contexts/WebSocket";

import type {
  EncodingStatus,
  EncodingStream,
  EncodingType,
  GetStreamsResponse,
  MetadataEncoding,
  OwnerEncodingJob,
} from "../api/EncoderAPI.types";
import type {
  FastAsset,
  GetMediaFastAssetsResponse,
} from "../api/MediaAPI.types";
import type { AppState } from "../reducers";

export interface EncodingProgressEvent {
  data: {
    notif_encoding_finished_bo: boolean;
    encoding_job_user_uuid: string;
    encoding_job_uuid: string;
    hosted_stream_uuids: string[];
    inserted_at: string;
    media_duration: number | null;
    media_label: string;
    media_owner_user_uuid: string;
    media_type: string;
    media_uuid: string;
    metadata: MetadataEncoding | null;
    owner: OwnerEncodingJob;
    progression: number;
    status: EncodingStatus;
    type: Exclude<EncodingType, "fast_hls">;
  };
  event: "start" | "progress" | "done" | "failed" | "new";
  subject: "encoding";
}

export type FastHlsProgressEvent = Omit<EncodingProgressEvent, "data"> & {
  data: Omit<EncodingProgressEvent["data"], "type"> & {
    type: "fast_hls";
    fast_asset_uuid: string;
  };
};

interface UseEncodingWSProps {
  mediaId: string;
}

function updateFastAsset(
  messageData: EncodingProgressEvent["data"] | FastHlsProgressEvent["data"],
  asset: GetMediaFastAssetsResponse["fast_assets"],
): Partial<FastAsset> | null {
  if (messageData.type === "fast_hls") {
    return {
      ...asset,
      uuid: messageData.fast_asset_uuid,
      hls_status: messageData.status,
      hls_encoding_progression: messageData.progression,
      mp4_encoding_progression: asset?.mp4_encoding_progression ?? 0,
      mp4_status: asset?.mp4_status ?? "queued",
    };
  }
  if (messageData.type === "fast_mp4") {
    return {
      ...asset,
      mp4_status: messageData.status,
      mp4_encoding_progression: messageData.progression,
      hls_encoding_progression: asset?.hls_encoding_progression ?? 0,
      hls_status: asset?.hls_status ?? "queued",
    };
  }
  return asset;
}

function updateEncodingStream(
  messageData: EncodingProgressEvent["data"],
  stream: EncodingStream,
): EncodingStream {
  const shouldUpdate =
    (messageData.type === "ott" &&
      messageData.hosted_stream_uuids.includes(stream.uuid)) ||
    (messageData.type === "d2g" && stream.type === "d2g");

  if (!shouldUpdate) return stream;

  return {
    ...stream,
    status: messageData.status,
    progression: messageData.progression,
  };
}

function useEncodingWS({ mediaId }: UseEncodingWSProps) {
  const { joinChannel, channels } = useContext(WebSocketContext);

  const queryClient = useQueryClient();

  const organizationUuid = useSelector(
    (state: AppState) => state.fetchReducer.orga_config.data.organization_uuid,
  );

  const userUuid = useSelector(
    (state: AppState) => state.fetchReducer.user.data.uuid,
  );

  const channelTopic = `editor:${organizationUuid}:${userUuid}`;
  const channel = useMemo(
    () => channels?.[channelTopic] ?? null,
    [channels, channelTopic],
  );

  const handleNotificationEventMessage = useCallback(
    (message: EncodingProgressEvent | FastHlsProgressEvent): void => {
      if (
        message.subject !== "encoding" ||
        (message.event !== "progress" && message.event !== "done") ||
        message.data.media_uuid !== mediaId
      )
        return;

      const getStreamsQuery = EncoderAPI.generateQuery({
        fn: EncoderAPI.getStreams,
        params: [mediaId],
      });

      const fastAssetsQuery = MediaAPI.generateQuery({
        fn: MediaAPI.getMediaFastAssets,
        params: [mediaId],
      });

      const { data } = message;

      if (data.type === "ott" || data.type === "d2g") {
        const prevData = queryClient.getQueryData<GetStreamsResponse>(
          getStreamsQuery.queryKey,
        );
        if (prevData) {
          queryClient.setQueryData<GetStreamsResponse>(
            getStreamsQuery.queryKey,
            {
              ...prevData,
              data: prevData.data.map((stream) =>
                updateEncodingStream(data, stream),
              ),
            },
          );
        }
      }

      if (data.type === "fast_hls" || data.type === "fast_mp4") {
        const prevData = queryClient.getQueryData<GetMediaFastAssetsResponse>(
          fastAssetsQuery.queryKey,
        );
        if (prevData) {
          queryClient.setQueryData<GetMediaFastAssetsResponse>(
            fastAssetsQuery.queryKey,
            {
              fast_assets: updateFastAsset(
                data,
                prevData.fast_assets,
              ) as GetMediaFastAssetsResponse["fast_assets"],
            },
          );
        }
      }
    },
    [mediaId, queryClient],
  );

  useEffect(() => {
    if (organizationUuid) {
      joinChannel({
        channelTopic,
      });
    }
  }, [joinChannel, channelTopic, organizationUuid]);

  useEffect(() => {
    if (!channel) return (): void => {};

    const notificationRef = channel.on(
      "notification",
      handleNotificationEventMessage,
    );

    return (): void => {
      channel.off("notification", notificationRef);
    };
  }, [channel, handleNotificationEventMessage]);
}

export default useEncodingWS;
